import { useEffect, type ReactNode } from 'react';
import { Color, Size, printAllowed, t, toolbarFor, type LinkPermission } from '@bms2/shared';
import { Button, Watermark } from '../controls';
import { Text } from '../Text';
import styles from './shared.module.css';

/**
 * File Viewer (piece 4): the screen a shared-file link's own permission
 * renders into. The component decides no permission of its own; the toolbar
 * is determined by the permission it is handed, so a View link has no
 * Download control in the DOM at all; print is intercepted through the same
 * check by overriding the browser's own print while the viewer is mounted;
 * View-only draws the diagonal watermark across the document body.
 */
export const fileViewerDocumentedStates = ['view', 'download'] as const;

export interface FileViewerProps {
  /** The permission the FileShareLink carries; never computed here. */
  permission: LinkPermission;
  fileName: string;
  onDownload: () => void;
  onPrint: () => void;
  watermarkText?: string;
  children: ReactNode;
}

export function FileViewer({ permission, fileName, onDownload, onPrint, watermarkText, children }: FileViewerProps) {
  const toolbar = toolbarFor(permission);

  useEffect(() => {
    if (printAllowed(permission)) return;
    const original = window.print;
    window.print = () => undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.print = original;
      window.removeEventListener('keydown', onKey, true);
    };
  }, [permission]);

  return (
    <section className={styles.viewer} data-file-viewer="true" data-permission={permission} aria-label={fileName}>
      <div className={styles.viewerBar}>
        <Text variant="small">{fileName}</Text>
        <span className={styles.viewerActions}>
          {toolbar.download ? <Button size={Size.Small} text={[t('viewer.download')]} actions={[onDownload]} /> : null}
          {toolbar.print ? <Button size={Size.Small} color={Color.Inactive} text={[t('viewer.print')]} actions={[onPrint]} /> : null}
          {toolbar.watermark ? <Text variant="caption">{t('viewer.viewOnly')}</Text> : null}
        </span>
      </div>
      <div className={styles.viewerBody} data-viewer-body="true">
        {children}
        {toolbar.watermark ? <Watermark text={watermarkText ?? t('watermark.viewOnly')} /> : null}
      </div>
    </section>
  );
}
